import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './lib/cn';

const skeleton = cva('bg-muted', {
  variants: {
    shape: {
      block: 'rounded-md',
      text: 'rounded-pill',
      circle: 'rounded-pill',
      card: 'rounded-xl',
    },
    animation: {
      /** Soft opacity pulse — the default. */
      pulse: 'animate-pulse',
      /** Left-to-right sweep — use for large media placeholders. */
      shimmer: 'shimmer',
      none: '',
    },
  },
  defaultVariants: { shape: 'block', animation: 'pulse' },
});

export interface SkeletonProps extends VariantProps<typeof skeleton> {
  className?: string;
  /** Convenience: render N stacked text lines with a shorter last line. */
  lines?: number;
}

/** Loading placeholder. Always show these instead of a blank region. */
export function Skeleton({ shape, animation, lines, className }: SkeletonProps) {
  if (lines && lines > 1) {
    return (
      <div className="w-full space-y-2" aria-hidden>
        {Array.from({ length: lines }, (_, i) => (
          <div
            key={`line-${i}`}
            className={cn(
              skeleton({ shape: 'text', animation }),
              'h-3',
              i === lines - 1 ? 'w-2/3' : 'w-full',
              className,
            )}
          />
        ))}
      </div>
    );
  }

  return <div aria-hidden className={cn(skeleton({ shape, animation }), 'h-4 w-full', className)} />;
}
